import { get } from "lodash";
import { Check, X } from "lucide-react";
import { memo, useCallback } from "react";
import { useLocation } from "wouter";

import { type OrderCancelResponseDTO } from "@/common/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
} from "@/common/components";
import { ROUTES } from "@/common/config";
import { formatDatetime, formatDate } from "@/common/lib";

interface CancellationTableProps {
  requests: OrderCancelResponseDTO[];
  isLoading: boolean;
  isPending?: boolean;
  onApprove?: (requestId: string) => void;
  onCancel?: (requestId: string) => void;
}

export const CancellationTable = memo(function CancellationTable({
  requests,
  isLoading,
  isPending,
  onApprove,
  onCancel,
}: CancellationTableProps) {
  const [, setLocation] = useLocation();

  const handleRowClick = useCallback(
    (requestId: string) => {
      setLocation(ROUTES.ORDER_ID.replace(":id", requestId));
    },
    [setLocation],
  );

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="caption">Загрузка запросов на отмену заявки...</p>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="caption">Запросы на отмену заявки отсутствуют</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Номер заявки</TableHead>
          <TableHead>Срок проведения</TableHead>
          <TableHead>Причина отмены</TableHead>
          <TableHead>Дата запроса</TableHead>
          <TableHead>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((request) => (
          <TableRow
            key={request.id}
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(get(request, "orderId", ""));
            }}
          >
            <TableCell className="font-medium align-top">{request.orderNumber}</TableCell>
            <TableCell className="align-top">
              <>
                <p className="text-sm text-nowrap">с {formatDatetime(request.tradeBegin)}</p>
                <p className="text-sm text-nowrap">по {formatDatetime(request.tradeEnd)}</p>
              </>
            </TableCell>
            <TableCell className="text-left align-top min-w-60">{request.comment || "-"}</TableCell>
            <TableCell className="align-top">{formatDate(request.creationDate)}</TableCell>
            <TableCell className="text-center align-top">
              <div className="flex items-center justify-center gap-2">
                <Button
                  size="sm"
                  className="btn-primary"
                  disabled={isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    onApprove?.(request.id!);
                  }}
                >
                  <Check className="h-4 w-4 mr-1" />
                  <span className="hidden md:block">Утвердить</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:text-white"
                  disabled={isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel?.(request.id!);
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  <span className="hidden md:block">Отклонить</span>
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
});
